import {
  UploadStatus,
  useVehicleGalleryContext,
} from '@/contexts/vehicle-gallery-provider';
import { useEffect, useState, type CSSProperties } from 'react';

/**
 * As as pert of FileUploadButton component, its responsibility is to display image upload
 * progress so user can have an idea of what is happening behind the scenes.
 * The following actions are covered by loading indicator:
 *  - Show upload image progress - uploading images to S3.
 *  - Show image processing progress - image that is uploaded in S3 does not exist as a metadata record in DB until Lambda fires,
 *    generates blur has of an image and then mark image as `COMPLETED`. This Lambda processing is what we refer to as "image processing step".
 *  - Show that images are being re-organized - before the upload images can be placed at any position within the gallery, because of which
 *    we need to fire last call to the API: assign new image-order index.
 */
export const UploadProgressBar = () => {
  const { uploadStatus, vehicleImages } = useVehicleGalleryContext();
  const [currentlyUploadedCount, setCurrentlyUploadedCount] = useState(0);
  const [totalAmountOfImagesForUpload, setTotalAmountOfImagesForUpload] =
    useState(0);

  // Watch for total image upload count
  useEffect(() => {
    if (uploadStatus === UploadStatus.UPLOADING) {
      setTotalAmountOfImagesForUpload(
        vehicleImages.filter((img) => img.type === 'upload_prepare').length,
      );
      return;
    }

    if (totalAmountOfImagesForUpload != 0) {
      setTotalAmountOfImagesForUpload(0);
    }
  }, [uploadStatus, vehicleImages]);

  // Update image upload counter
  useEffect(() => {
    if (uploadStatus === UploadStatus.UPLOADING) {
      const uploadedImages = vehicleImages.filter((img) => img.uploaded);
      setCurrentlyUploadedCount(uploadedImages.length);
    }
  }, [vehicleImages, uploadStatus]);

  // Self invoking
  const progressBarStyles = ((): CSSProperties => {
    if (uploadStatus === UploadStatus.UPLOADING) {
      return {
        transform: `scaleX(${currentlyUploadedCount / totalAmountOfImagesForUpload})`,
        transformOrigin: 'left',
      };
    }

    return {
      transform: `scaleX(1)`,
      transformOrigin: 'left',
    };
  })();

  return (
    <div
      className="progress-info flex flex-col items-center"
      style={{ width: '100%', maxWidth: 400 }}
    >
      <div className="progress-bar h-3 overflow-hidden rounded w-full border-2 border-green-500">
        <div
          className="progress-step w-full h-full bg-green-500 transition-transform duration-300 ease-in-out"
          style={progressBarStyles}
        ></div>
      </div>
      {uploadStatus === UploadStatus.UPLOADING && (
        <span>
          Uploaded {currentlyUploadedCount} out {totalAmountOfImagesForUpload}{' '}
          images
        </span>
      )}

      {uploadStatus === UploadStatus.PROCESSING && (
        <span>Processing images...</span>
      )}

      {uploadStatus === UploadStatus.MARKING_FEATURED && (
        <span>Marking featured image...</span>
      )}

      {uploadStatus === UploadStatus.RE_ORDERING && (
        <span>Re-ordering images...</span>
      )}

      {uploadStatus === UploadStatus.FINALIZING && <span>Finalizing...</span>}
    </div>
  );
};
